import Header from '../components/Header';
import Footer from '../components/Footer';

const sections = [
	{
		id: 'about',
		title: '1. About This Policy',
		content: (
			<p className="text-slate-600 leading-relaxed">
				This Cookie Policy explains how Viance uses cookies and similar technologies on its websites and web applications. It should be read together with our{' '}
				<a className="text-cyan-600 hover:underline" href="/privacy">Privacy Policy</a>.
			</p>
		),
	},
	{
		id: 'what-are-cookies',
		title: '2. What Are Cookies?',
		content: (
			<p className="text-slate-600 leading-relaxed">
				Cookies are small text files placed on your device to store data that can be recalled by a web server in the domain that placed them. They enable core functionality, personalisation, analytics and security across our digital properties.
			</p>
		),
	},
	{
		id: 'cookies-we-use',
		title: '3. Cookies We Use',
		content: (
			<ul className="list-disc pl-6 space-y-2 text-slate-600">
				<li>
					<span className="font-semibold text-slate-800">Strictly Necessary:</span> Authentication state, load balancing, session continuity.
				</li>
				<li>
					<span className="font-semibold text-slate-800">Functional:</span> UI preferences, remembered form inputs, language selection.
				</li>
				<li>
					<span className="font-semibold text-slate-800">Analytics / Performance:</span> Traffic patterns, feature usage, error diagnostics (aggregated &amp; anonymised).
				</li>
				<li>
					<span className="font-semibold text-slate-800">Security:</span> Fraud detection tokens, rate‑limiting markers.
				</li>
				<li>
					<span className="font-semibold text-slate-800">Experimental / A/B:</span> Variant allocation for UX performance trials.
				</li>
			</ul>
		),
	},
	{
		id: 'third-party',
		title: '4. Third-Party Technologies',
		content: (
			<p className="text-slate-600 leading-relaxed">
				We may leverage trusted analytics or infrastructure partners that set their own cookies (e.g., performance metrics). These partners are contractually restricted from using data for unrelated profiling purposes.
			</p>
		),
	},
	{
		id: 'your-controls',
		title: '5. Your Controls',
		content: (
			<ul className="list-disc pl-6 space-y-1 text-slate-600">
				<li>Browser settings: block or delete cookies (some site features may degrade).</li>
				<li>In‑product toggles (when offered) for analytics or personalisation categories.</li>
				<li>Opt‑out links provided by some analytics vendors.</li>
				<li>Private / incognito mode to limit persistence.</li>
			</ul>
		),
	},
	{
		id: 'retention',
		title: '6. Retention',
		content: (
			<p className="text-slate-600 leading-relaxed">
				Cookies persist for session length or a defined expiration (typically 1 day to 12 months) unless deleted earlier by you. Aggregated analytics derived from cookies may be retained longer in de-identified form.
			</p>
		),
	},
	{
		id: 'do-not-track',
		title: '7. Do Not Track',
		content: (
			<p className="text-slate-600 leading-relaxed">
				Our services currently do not alter behaviour in response to browser DNT signals due to the lack of an industry standard. We continue to monitor emerging specifications and will update this policy accordingly.
			</p>
		),
	},
	{
		id: 'updates',
		title: '8. Updates to This Policy',
		content: (
			<p className="text-slate-600 leading-relaxed">
				We may revise this Cookie Policy for operational, legal, or regulatory reasons. Material changes will be indicated via an update notice on our website. Continued use after the update implies acceptance.
			</p>
		),
	},
	{
		id: 'contact',
		title: '9. Contact',
		content: (
			<div className="space-y-2 text-slate-600">
				<p className="font-semibold text-slate-800">VIANCE HEALTHCARE SYSTEMS PRIVATE LIMITED</p>
				<p>Sulochana Apartments, Income Tax Lane, Pune – 411004, Maharashtra, India</p>
				<p>
					Email:{' '}
					<a className="text-cyan-600 hover:underline" href="mailto:admin@viance.co.in">
						admin@viance.co.in
					</a>
				</p>
			</div>
		),
	},
];

function CookiePolicy() {
	return (
		<div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100 to-slate-100 text-slate-900">
			<Header />
			<main className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
				{/* Page header */}
				<div className="mb-10">
					<span className="inline-block rounded-full bg-cyan-100 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-cyan-700">
						Legal
					</span>
					<h1 className="mt-4 text-4xl font-extrabold tracking-tight text-slate-900">
						Cookie Policy
					</h1>
					<p className="mt-3 max-w-2xl text-slate-600">
						How and why Viance uses cookies &amp; similar technologies across its digital properties.
					</p>
					<p className="mt-2 text-sm text-slate-400">Last updated: 2026</p>
				</div>

				{/* Table of contents */}
				<nav className="mb-10 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
					<h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-cyan-700">Contents</h2>
					<ol className="space-y-1 text-sm text-slate-600">
						{sections.map((s) => (
							<li key={s.id}>
								<a href={`#${s.id}`} className="hover:text-cyan-600 transition-colors">
									{s.title}
								</a>
							</li>
						))}
					</ol>
				</nav>

				{/* Sections */}
				<div className="space-y-10 rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
					{sections.map((s, i) => (
						<section key={s.id} id={s.id} className={i !== 0 ? 'border-t border-slate-100 pt-8' : ''}>
							<h2 className="mb-4 text-xl font-bold text-slate-900">{s.title}</h2>
							{s.content}
						</section>
					))}
				</div>
			</main>
			<Footer />
		</div>
	);
}

export default CookiePolicy;
